import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { formatDateTime } from '@/lib/format'
import { messages } from '@/lib/messages'
import type { AuditLog } from '@/types/models'

const text = messages.audit

const auditActionLabel = (action: string) => text.actions[action] ?? action

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-text-secondary text-xs">{label}</dt>
      <dd className="text-sm break-words">{children}</dd>
    </div>
  )
}

/** Nesne/dizi değerleri JSON; düz değerler olduğu gibi. */
function metaValue(value: unknown): string {
  if (value === null || value === undefined) return messages.common.empty
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

/** Denetim satırının ayrıntısı (docs/ADMIN-PANEL-PLAN.md Faz 5); satır sayfadaki veriden gelir. */
export function AuditDetailPanel({ log, onClose }: { log: AuditLog | null; onClose: () => void }) {
  const meta = Object.entries(log?.meta ?? {})

  return (
    <Sheet open={log !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right">
        <SheetHeader>
          <SheetTitle>{log ? auditActionLabel(log.action) : text.detail.title}</SheetTitle>
          <SheetDescription>{log ? formatDateTime(log.occurred_at) : ''}</SheetDescription>
        </SheetHeader>
        <SheetBody>
          {log && (
            <div className="flex flex-col gap-6">
              <dl className="flex flex-col gap-3">
                <Field label={text.columns.action}>
                  <code className="text-text-secondary text-xs">{log.action}</code>
                </Field>
                <Field label={text.columns.actor_email}>
                  {log.actor_id ? (
                    <>
                      {log.actor_full_name && <span className="block">{log.actor_full_name}</span>}
                      <span className="text-text-secondary">{log.actor_email}</span>
                    </>
                  ) : (
                    messages.common.unknown
                  )}
                </Field>
                <Field label={text.columns.target}>
                  {log.target_type ? (
                    <span className="flex flex-wrap items-center gap-2">
                      <code className="text-xs">{`${log.target_type}:${log.target_id ?? ''}`}</code>
                      {log.target_type === 'user' && log.target_id && (
                        <Button
                          variant="secondary"
                          size="sm"
                          nativeButton={false}
                          render={<Link to={`/yonetim/kullanicilar?u=${log.target_id}`} />}
                        >
                          {text.detail.openUser}
                        </Button>
                      )}
                    </span>
                  ) : (
                    messages.common.empty
                  )}
                </Field>
                <Field label={text.columns.ip}>{log.ip ?? messages.common.empty}</Field>
                <Field label={text.columns.user_agent}>
                  {log.user_agent ?? messages.common.empty}
                </Field>
              </dl>

              <section className="flex flex-col gap-2">
                <h3 className="text-text-secondary text-xs font-medium">{text.detail.meta}</h3>
                {meta.length === 0 ? (
                  <p className="text-text-muted text-sm">{text.detail.noMeta}</p>
                ) : (
                  <dl className="border-border divide-border divide-y rounded-md border">
                    {meta.map(([key, value]) => (
                      <div key={key} className="flex gap-3 px-3 py-2 text-sm">
                        <dt className="text-text-secondary w-40 shrink-0 break-all">{key}</dt>
                        <dd className="min-w-0 flex-1 break-all">{metaValue(value)}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </section>
            </div>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}
